import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export type RangeOption =
  | 'Today'
  | 'Yesterday'
  | 'Weekly'
  | 'Monthly'
  | 'Yearly';

export type ReportId =
  | 'EMPLOYEES'
  | 'BEST_SELLING_PRODUCTS'
  | 'SALES_REPORT'
  | 'YEARLY_SALES_TOTALS'
  | 'DAILY_STOCKS'
  | 'MONTHLY_SALES_REPORT'
  | 'UPDATED_PRODUCTS';

export interface ReportPagination {
  page: number;
  pageSize: number;
  total: number;
}

export interface ReportConfig {
  id: ReportId | null;
  title: string;
  columns: string[];
  placeholder?: string;
  showFilter: boolean;
  showMeta: boolean;
}

export interface ReportPageState {
  config: ReportConfig;
  loading: boolean;
  error?: string | null;
  rows: any[];
  pagination: ReportPagination;
  meta?: Record<string, any> | null;
  range: { selected: RangeOption; startISO?: string; endISO?: string };
}

const initialState: ReportPageState = {
  config: {
    id: null,
    title: '',
    columns: [],
    placeholder: '',
    showFilter: false,
    showMeta: false,
  },
  loading: false,
  error: null,
  rows: [],
  pagination: { page: 1, pageSize: 100, total: 0 },
  meta: null,
  range: { selected: 'Weekly' },
};

const reportsTableSlice = createSlice({
  name: 'backofficeReportsTable',
  initialState,
  reducers: {
    setReportConfig: (state, action: PayloadAction<Partial<ReportConfig>>) => {
      state.config = { ...state.config, ...action.payload } as ReportConfig;
    },
    setReportLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setReportError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setReportRows: (
      state,
      action: PayloadAction<{
        rows: any[];
        pagination?: Partial<ReportPagination>;
        meta?: Record<string, any> | null;
      }>,
    ) => {
      state.rows = action.payload.rows || [];
      if (action.payload.pagination) {
        state.pagination = {
          ...state.pagination,
          ...action.payload.pagination,
        };
      }
      if (action.payload.meta !== undefined) state.meta = action.payload.meta;
    },
    setReportPagination: (
      state,
      action: PayloadAction<Partial<ReportPagination>>,
    ) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    setReportRange: (
      state,
      action: PayloadAction<{
        selected?: RangeOption;
        startISO?: string;
        endISO?: string;
      }>,
    ) => {
      state.range = { ...state.range, ...action.payload } as any;
    },
    clearReport: state => {
      state.rows = [];
      state.error = null;
      state.pagination = { page: 1, pageSize: 100, total: 0 };
      state.meta = null;
    },
  },
});

export const {
  setReportConfig,
  setReportLoading,
  setReportError,
  setReportRows,
  setReportPagination,
  setReportRange,
  clearReport,
} = reportsTableSlice.actions;

export default reportsTableSlice.reducer;
